import { Injectable } from '@angular/core';
import { map, Observable, ReplaySubject, takeWhile } from 'rxjs';
import { User } from '../../../models/user.model';
import { AuthService } from '@auth0/auth0-angular';
import { environment } from '../../../../environments/environment';
import { arraysEqual, hasRequiredRole, isEmptyArray, isNullOrUndefined } from '../common-utils/common-utils';
import { HttpClient, HttpParams } from '@angular/common/http';
import { AAT_IM_APIMapping } from '../../constants/api-mapping.constant';
import { PaginatedUsers } from '../../../models/user-response-dto.model';
import { UserResponseDTO } from '../../../models/user-response-dto.model';
import { ApiResponse } from '../../../models/api-response';
import { PaginatedDeviceHistory } from '../../../models/user-eula-detail.model';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { ErrorMessages } from '../../constants/project.constant';

@Injectable({
  providedIn: 'root'
})
export class UserService {
  public userSubject: ReplaySubject<User> = new ReplaySubject<User>();
  private readonly user: User = {} as User;
  aatImApiRootUrl = environment.urlMapping.AAT_IM_API;
  public isAuthorized = false;
  private currentRoles: string[] = [];
  private monitoring = false;

  constructor(private readonly authService: AuthService,
    private readonly http: HttpClient,
    private readonly router: Router,
    private readonly toastr: ToastrService
  ) { }

  setUser() {
    this.authService.idTokenClaims$
      .pipe(takeWhile(() => isNullOrUndefined(this.user.id)))
      .subscribe(claims => {
        if (!isNullOrUndefined(claims)) {
          this.user.id = claims?.["sub"] ?? '';
          this.user.name = claims?.name ?? '';
          this.user.roles = claims?.[environment.auth.roleClaimNameSpace] ?? [];
          this.currentRoles = this.user.roles;
          this.isAuthorized = hasRequiredRole(this.user.roles);
          this.userSubject.next(this.user);
        }
      });
  }

  getUser() {
    return this.userSubject.asObservable();
  }

  monitorTokenChanges(): void {
    if (this.monitoring) return;
    this.monitoring = true;

    this.authService.idTokenClaims$.subscribe(claims => {
      if (isNullOrUndefined(claims)) return;

      const newRoles: string[] = claims?.[environment.auth.roleClaimNameSpace] ?? [];
      const rolesChanged = !arraysEqual(this.currentRoles, newRoles);

      if (rolesChanged && this.currentRoles.length > 0) {
        this.currentRoles = newRoles;
        this.user.roles = newRoles;
        this.isAuthorized = hasRequiredRole(newRoles);
        this.userSubject.next(this.user);

        if (isEmptyArray(newRoles)) {
          this.router.navigate(['/access-denied']);
        } else {
          this.toastr.warning(ErrorMessages.permissionsChanged);
          this.router.navigate(['/user']);
        }
      } else {
        this.currentRoles = newRoles;
      }
    });
  }

  createOrUpdateUser(user: any) {
    return this.http.post(this.aatImApiRootUrl + AAT_IM_APIMapping.USER, user, { observe: 'response' });
  }

  getUsers(page: number, size: number, search: string = ''): Observable<PaginatedUsers> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString());

    if (search) {
      params = params.set('search', search);
    }
    return this.http.get<PaginatedUsers>(this.aatImApiRootUrl + AAT_IM_APIMapping.USER,
      { params: params, observe: 'response' })
      .pipe(
        map((response) => this.normalizePaginatedUsers(response.body))
      );
  }

  getUserById(userId: string): Observable<UserResponseDTO> {
    return this.http.get<UserResponseDTO>(
      this.aatImApiRootUrl + AAT_IM_APIMapping.USER_BY_AUTH0_ID.replace(':auth0Id', encodeURIComponent(userId)),
      { observe: 'response' }
    ).pipe(
      map((response) => {
        if (response.body === null) {
          throw new Error(`No user found for id: ${userId}`);
        }
        return this.normalizeUser(response.body);
      })
    );
  }

  clearRegisteredDevice(userId: string): Observable<ApiResponse> {
    return this.http.put<ApiResponse>(this.aatImApiRootUrl + AAT_IM_APIMapping.CLEAR_DEVICE.replace(':auth0Id', encodeURIComponent(userId)), {}, { observe: 'response' })
    .pipe(
      map(response => response.body as ApiResponse)
    );
  }

  getDeviceHistory(userId: string, page: number = 0, size: number = 10): Observable<PaginatedDeviceHistory> {
    const params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString());
    return this.http.get<PaginatedDeviceHistory>(
      this.aatImApiRootUrl + AAT_IM_APIMapping.DEVICE_HISTORY.replace(':auth0Id', encodeURIComponent(userId)),
      { params, observe: 'response' }
    ).pipe(
      map(response => response.body ?? { content: [], page: 0, size: 0, totalElements: 0, totalPages: 0 })
    );
  }

  private normalizePaginatedUsers(users: PaginatedUsers | null): PaginatedUsers {
    if (!users) {
      return { content: [], totalElements: 0 };
    }
    return {
      ...users,
      content: users.content?.map((user) => this.normalizeUser(user)) ?? [],
      totalElements: users.totalElements ?? 0
    };
  }

  private normalizeUser(user: UserResponseDTO): UserResponseDTO {
    return {
      ...user,
      deviceRegistered: user.deviceRegistered ?? null
    };
  }

}
